import React, { useEffect, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface Slide {
  title: string;
  subtitle: string;
  image: string;
  buttonText?: string;
  buttonLink?: string;
}
interface HeroCarouselProps {
  slides: Slide[];
  autoPlayInterval?: number;
}
const HeroCarousel: React.FC<HeroCarouselProps> = ({
  slides,
  autoPlayInterval = 5000
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, autoPlayInterval);
    return () => clearInterval(timer);
  }, [currentSlide, autoPlayInterval]);
  const nextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide(prev => (prev + 1) % slides.length);
    setTimeout(() => setIsAnimating(false), 700);
  };
  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsAnimating(false), 700);
  };
  const goToSlide = (index: number) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 700);
  };
  return <div className="relative w-full h-[600px] md:h-[700px] overflow-hidden bg-gray-900" data-id="element-138">
      {slides.map((slide, index) => <div key={index} className={`absolute inset-0 transition-all duration-700 ease-in-out ${index === currentSlide ? 'opacity-100 translate-x-0 scale-100' : index < currentSlide ? 'opacity-0 -translate-x-full scale-95' : 'opacity-0 translate-x-full scale-95'}`} data-id="element-139">
          <div className="absolute inset-0 bg-cover bg-center animate-ken-burns" style={{
        backgroundImage: `url(${slide.image})`
      }} data-id="element-140" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/90 via-primary-800/80 to-transparent" data-id="element-141" />
          <div className="relative h-full flex items-center" data-id="element-142">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-143">
              <div className="max-w-3xl" data-id="element-144">
                <h1 className={`text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 transition-all duration-1000 ${index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
              transitionDelay: '200ms'
            }} data-id="element-145">
                  {slide.title}
                </h1>
                <p className={`text-xl md:text-2xl text-white/90 mb-8 transition-all duration-1000 ${index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
              transitionDelay: '400ms'
            }} data-id="element-146">
                  {slide.subtitle}
                </p>
                {slide.buttonText && slide.buttonLink && <a href={slide.buttonLink} className={`inline-block bg-white text-primary-600 font-semibold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ${index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{
              transitionDelay: '600ms'
            }} data-id="element-147">
                    {slide.buttonText}
                  </a>}
              </div>
            </div>
          </div>
        </div>)}
      <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 hover:scale-110 z-10" aria-label="Previous slide" data-id="element-148">
        <ChevronLeftIcon className="w-6 h-6" data-id="element-149" />
      </button>
      <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 hover:scale-110 z-10" aria-label="Next slide" data-id="element-150">
        <ChevronRightIcon className="w-6 h-6" data-id="element-151" />
      </button>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-10" data-id="element-152">
        {slides.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'w-12 bg-white' : 'w-2 bg-white/50 hover:bg-white/75'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-153" />)}
      </div>
    </div>;
};
export default HeroCarousel;